import { useEffect, useRef } from 'react';
import { sendSessionPing } from '../services/authService';

/**
 * useSessionHeartbeat
 * Periodically tells the backend "this session is still alive" so that sessions
 * ending by tab-close (which never send a LOGOUT) still contribute time to the
 * engagement report.
 *
 * The heartbeat is deliberately non-destructive: it only records a last-seen
 * timestamp and never ends the session. That matters because the session_id is
 * shared across browser tabs — a "session ended" signal from one closing tab
 * would wrongly end a session another tab is still using.
 *
 * Behaviour:
 *   - Sends one ping on mount, then every `intervalMs` while enabled.
 *   - Sends a ping the moment the tab is hidden (tab switch, minimize, or the
 *     first phase of a tab close), which is the last reliable moment to capture.
 *   - Skips pings while the tab is hidden to avoid pointless traffic, resuming
 *     immediately when it becomes visible again.
 *
 * Worst-case data loss is a single interval (default ~60s) — far better than
 * losing an entire untracked session.
 *
 * @param {boolean} enabled     only ping while authenticated
 * @param {number}  intervalMs  heartbeat cadence (default 60s)
 */
export function useSessionHeartbeat(enabled, intervalMs = 60 * 1000) {
  const timerRef = useRef(null);

  useEffect(() => {
    if (!enabled) return undefined;

    const ping = () => {
      // No point pinging while backgrounded; the visibility handler already
      // fired one as the tab went hidden.
      if (document.visibilityState === 'visible') sendSessionPing();
    };

    // Immediate ping so a very short session still registers a last-seen mark.
    sendSessionPing();
    timerRef.current = setInterval(ping, intervalMs);

    const handleVisibility = () => {
      if (document.visibilityState === 'hidden') {
        // Last reliable chance to record the session before the tab may close.
        sendSessionPing();
      } else {
        // Back in view — mark alive right away rather than waiting a full cycle.
        sendSessionPing();
      }
    };

    document.addEventListener('visibilitychange', handleVisibility);

    return () => {
      clearInterval(timerRef.current);
      document.removeEventListener('visibilitychange', handleVisibility);
    };
  }, [enabled, intervalMs]);
}
